import React from 'react';
import {
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import cn from 'classnames';
import {t} from '@lingui/core/macro';

import Close from 'shared/ui/icons/General/Close';

import {PALETTE} from 'shared/config/model/palette';

import styles from './MonitorOrderList.module.scss';

export interface IMonitorOrderItem {
    id: string;
    label: string;
}

interface IMonitorOrderListProps {
    items: IMonitorOrderItem[];
    /* eslint-disable no-unused-vars */
    onReorder: (ids: string[]) => void;
    onRemove: (id: string) => void;
    /* eslint-enable no-unused-vars */
}

const DragHandle: React.FC = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="6" cy="4" r="1.2" fill={PALETTE.neutral}/>
        <circle cx="10" cy="4" r="1.2" fill={PALETTE.neutral}/>
        <circle cx="6" cy="8" r="1.2" fill={PALETTE.neutral}/>
        <circle cx="10" cy="8" r="1.2" fill={PALETTE.neutral}/>
        <circle cx="6" cy="12" r="1.2" fill={PALETTE.neutral}/>
        <circle cx="10" cy="12" r="1.2" fill={PALETTE.neutral}/>
    </svg>
);

interface ISortableRowProps {
    item: IMonitorOrderItem;
    /* eslint-disable-next-line no-unused-vars */
    onRemove: (id: string) => void;
}

const SortableRow: React.FC<ISortableRowProps> = ({item, onRemove}) => {
    const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({id: item.id});

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={cn(styles.row, isDragging && styles.dragging)}
        >
            <span
                className={styles.handle}
                {...attributes}
                {...listeners}
                aria-label={t`Перетащить`}
            >
                <DragHandle/>
            </span>
            <span className={styles.label}>{item.label}</span>
            <span
                className={styles.remove}
                onClick={() => onRemove(item.id)}
                aria-label={t`Убрать`}
            >
                <Close/>
            </span>
        </div>
    );
};

const MonitorOrderList: React.FC<IMonitorOrderListProps> = ({items, onReorder, onRemove}) => {
    const sensors = useSensors(
        useSensor(PointerSensor, {activationConstraint: {distance: 5}}),
        useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates}),
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const {active, over} = event;
        if (!over || active.id === over.id) {
            return;
        }
        const oldIndex = items.findIndex((it) => it.id === active.id);
        const newIndex = items.findIndex((it) => it.id === over.id);
        if (oldIndex === -1 || newIndex === -1) {
            return;
        }
        onReorder(arrayMove(items, oldIndex, newIndex).map((it) => it.id));
    };

    if (items.length === 0) {
        return null;
    }

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={items.map((it) => it.id)} strategy={verticalListSortingStrategy}>
                <div className={styles.list}>
                    {items.map((item) => (
                        <SortableRow key={item.id} item={item} onRemove={onRemove}/>
                    ))}
                </div>
            </SortableContext>
        </DndContext>
    );
};

export default MonitorOrderList;
